import { formatCurrency, formatPercent, formatNumber, formatPrice } from '@/lib/format';
import type { Position } from '@/types';

interface PositionRowProps {
	position: Position;
}

export function PositionRow({ position }: PositionRowProps) {
	const direction = position.isLong ? 1 : -1;
	const pnlPercent = ((position.markPrice - position.entryPrice) / position.entryPrice) * direction;
	const isProfit = position.unrealizedPnl >= 0;

	// SL/TP are computed from decisionEntryPrice (ENTRY candle close), which may
	// differ from the Orderly average fill (entryPrice). Distance must use the
	// same base the bot triggers on.
	const slBase = position.decisionEntryPrice ?? position.entryPrice;
	const tpBase = position.decisionEntryPrice ?? position.entryPrice;
	const slDistancePct =
		position.stopLossPrice != null
			? ((position.stopLossPrice - slBase) / slBase) * direction
			: null;
	const tpDistancePct =
		position.takeProfitPrice != null
			? ((position.takeProfitPrice - tpBase) / tpBase) * direction
			: null;

	return (
		<tr className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors">
			<td className="py-3 px-4">
				<div className="font-medium text-text">
					{position.symbol.replace('PERP_', '').replace('_USDC', '')}
				</div>
			</td>
			<td className="py-3 px-4">
				<span
					className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
						position.isLong ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
					}`}
				>
					{position.isLong ? 'LONG' : 'SHORT'}
				</span>
			</td>
			<td className="py-3 px-4 text-text">{formatNumber(position.size, 4)}</td>
			<td className="py-3 px-4 text-text-muted">{formatPrice(position.entryPrice)}</td>
			<td className="py-3 px-4 text-text">{formatPrice(position.markPrice)}</td>
			<td className="py-3 px-4 text-text">
				{formatCurrency(position.size * position.markPrice, 2)}
			</td>
			<td className={`py-3 px-4 font-medium ${isProfit ? 'text-success' : 'text-danger'}`}>
				{formatCurrency(position.unrealizedPnl, 2)}
				<span className="text-xs ml-1">({formatPercent(pnlPercent)})</span>
			</td>
			<td className="py-3 px-4 text-sm">
				{position.stopLossPrice != null ? (
					<div>
						<span className="text-danger font-medium">{formatPrice(position.stopLossPrice)}</span>
						{slDistancePct != null && (
							<span className="text-xs text-text-muted ml-1">({formatPercent(slDistancePct)})</span>
						)}
					</div>
				) : (
					<span className="text-text-muted">-</span>
				)}
			</td>
			<td className="py-3 px-4 text-sm">
				{position.takeProfitPrice != null ? (
					<div>
						<span className="text-success font-medium">
							{formatPrice(position.takeProfitPrice)}
						</span>
						{tpDistancePct != null && (
							<span className="text-xs text-text-muted ml-1">({formatPercent(tpDistancePct)})</span>
						)}
					</div>
				) : (
					<span className="text-text-muted">-</span>
				)}
			</td>
		</tr>
	);
}
